/**
 * The desk inbox — a compact preview of Messages.
 *
 * Reading and replying live on the Messages page (/messages); this section is
 * the glance: top conversations with unread badges, one click deep-linking
 * into the thread (?c=). Chatwoot stays plumbing underneath — CMs never
 * work in its UI.
 */
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Inbox, ArrowUpRight, Megaphone } from 'lucide-react';
import { coworkApi } from '@/cowork/omniad/api/coworkApi';
import { cn } from '@/components/ui/utils';

const INBOX_TINT: Record<string, string> = {
  'Task Threads': 'bg-[#2a5fb0]/10 text-[#2a5fb0]',
  'Agent Chat': 'bg-[#0d8a5f]/10 text-[#0d8a5f]',
  'System Notifications': 'bg-gray-100 text-gray-500',
  'User Messaging': 'bg-[#be43ff]/10 text-[#be43ff]',
};

export function InboxSection() {
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ['cowork', 'cm', 'inbox'],
    queryFn: () => coworkApi.cm.inbox(),
    refetchInterval: 90_000,
    staleTime: 45_000,
  });

  if (!data?.connected || (data.conversations ?? []).length === 0) return null;

  return (
    <div className="mt-3">
      <div className="mb-1.5 flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 text-[12px] uppercase tracking-wide text-[#667085]" style={{ fontWeight: 600 }}>
          <Inbox className="h-3.5 w-3.5" aria-hidden /> Inbox
        </h3>
        <button
          type="button"
          onClick={() => navigate('/messages')}
          className="flex items-center gap-1 text-[12px] text-[#be43ff] hover:underline"
          style={{ fontWeight: 600 }}
        >
          Open messages <ArrowUpRight className="h-3 w-3" aria-hidden />
        </button>
      </div>
      <div className="space-y-1.5">
        {data.conversations.slice(0, 5).map(c => (
          <button
            key={c.id}
            type="button"
            onClick={() => navigate(`/messages?c=${c.id}`)}
            className="flex w-full items-center gap-2 rounded-xl bg-white px-3 py-2 text-left shadow-sm transition-all hover:shadow-md"
          >
            <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-semibold', INBOX_TINT[c.inbox] ?? 'bg-gray-100 text-gray-500')}>
              {c.inbox}
            </span>
            <span className="min-w-0 flex-1 truncate text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>
              {c.title}
              {c.lastMessage && <span className="ml-1.5 font-normal text-[#98a2b3]">{c.lastMessage}</span>}
            </span>
            {c.campaignId && <Megaphone className="h-3 w-3 shrink-0 text-[#be43ff]" aria-label="Campaign thread" />}
            {c.unread > 0 && <span className="rounded-full bg-[#be43ff] px-1.5 text-[10px] font-bold text-white">{c.unread}</span>}
            <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-gray-300" aria-hidden />
          </button>
        ))}
      </div>
    </div>
  );
}
